// GlobalDateField.tsx
import React from 'react';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';

interface GlobalDateFieldProps {
  label?: string;
  value: Nullable<Date | Date[] | (Date | null)[]>;
  onChange: (value: Nullable<Date | Date[] | (Date | null)[]>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  showTime?: boolean;
  timeOnly?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  showButtonBar?: boolean;
  selectionMode?: 'single' | 'multiple' | 'range';
  inline?: boolean;
  monthNavigator?: boolean;
  yearNavigator?: boolean;
  yearRange?: string;
}

export const GlobalDateField: React.FC<GlobalDateFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  required = false,
  disabled = false,
  error = '',
  icon = 'pi pi-calendar',
  fullWidth = true,
  showTime = false,
  timeOnly = false,
  dateFormat = 'dd/mm/yy',
  minDate,
  maxDate,
  showButtonBar = false,
  selectionMode = 'single',
  inline = false,
  monthNavigator = false,
  yearNavigator = false,
  yearRange = '2000:2030',
}) => {
  const handleChange = (e: any) => {
    onChange(e.value);
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#ef4444' : '#1e3a8a',
          marginBottom: '8px'
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8',
              fontSize: '16px',
              pointerEvents: 'none',
              zIndex: 1
            }}
          ></i>
        )}
        
        <Calendar
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          showTime={showTime}
          timeOnly={timeOnly}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          showButtonBar={showButtonBar}
          showIcon={false}
          selectionMode={selectionMode}
          inline={inline}
          monthNavigator={monthNavigator}
          yearNavigator={yearNavigator}
          yearRange={yearRange}
          style={{
            width: '100%'
          }}
          panelStyle={{
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            border: '1px solid #e5e7eb',
            marginTop: '4px'
          }}
          className="global-date-field"
        />
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '12px',
          color: '#ef4444',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-date-field .p-calendar {
          width: 100%;
        }

        .global-date-field .p-inputtext {
          width: 100%;
          height: 44px;
          padding: 12px 14px;
          padding-left: 42px;
          border: 1px solid ${error ? '#ef4444' : '#e5e7eb'};
          border-radius: 10px;
          font-size: 13px !important;
          background: ${disabled ? '#f9fafb' : 'white'};
          color: ${disabled ? '#9ca3af' : '#1f2937'} !important;
          transition: all 0.2s ease;
        }
        
        .global-date-field .p-inputtext::placeholder {
          color: #9ca3af !important;
          font-size: 13px !important;
        }

        .global-date-field .p-inputtext:enabled:focus {
          outline: 0;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }

        .global-date-field .p-inputtext:enabled:hover {
          border-color: #3b82f6 !important;
        }

        .global-date-field .p-datepicker {
          padding: 12px;
        }

        .global-date-field .p-datepicker .p-datepicker-header {
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px 8px 0 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .global-date-field .p-datepicker .p-datepicker-header .p-datepicker-title {
          margin: 0;
          color: #1e3a8a;
          font-weight: 600;
        }

        .global-date-field .p-datepicker .p-datepicker-header .p-datepicker-prev,
        .global-date-field .p-datepicker .p-datepicker-header .p-datepicker-next {
          color: #94a3b8;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .global-date-field .p-datepicker .p-datepicker-header .p-datepicker-prev:hover,
        .global-date-field .p-datepicker .p-datepicker-header .p-datepicker-next:hover {
          background: rgba(59, 130, 246, 0.08);
          color: #3b82f6;
        }

        .global-date-field .p-datepicker table td {
          padding: 4px;
        }

        .global-date-field .p-datepicker table td > span {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          transition: all 0.15s ease;
          color: #1f2937;
          font-size: 13px;
          font-weight: 500;
        }

        .global-date-field .p-datepicker table td > span:hover {
          background: rgba(59, 130, 246, 0.08);
          color: #1e3a8a;
        }

        .global-date-field .p-datepicker table td > span.p-highlight {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .global-date-field .p-datepicker table td.p-datepicker-today > span {
          background: rgba(59, 130, 246, 0.1);
          color: #1e3a8a;
          font-weight: 600;
        }

        .global-date-field .p-datepicker table td.p-datepicker-today > span.p-highlight {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
        }

        .global-date-field .p-datepicker .p-monthpicker .p-monthpicker-month,
        .global-date-field .p-datepicker .p-yearpicker .p-yearpicker-year {
          border-radius: 8px;
          transition: all 0.15s ease;
          color: #1f2937;
          font-weight: 500;
        }

        .global-date-field .p-datepicker .p-monthpicker .p-monthpicker-month:hover,
        .global-date-field .p-datepicker .p-yearpicker .p-yearpicker-year:hover {
          background: rgba(59, 130, 246, 0.08);
          color: #1e3a8a;
        }

        .global-date-field .p-datepicker .p-monthpicker .p-monthpicker-month.p-highlight,
        .global-date-field .p-datepicker .p-yearpicker .p-yearpicker-year.p-highlight {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          font-weight: 600;
        }

        .global-date-field .p-datepicker .p-timepicker {
          border-top: 1px solid #e5e7eb;
          padding: 12px;
          margin-top: 8px;
        }

        .global-date-field .p-datepicker .p-timepicker button {
          color: #94a3b8;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .global-date-field .p-datepicker .p-timepicker button:hover {
          background: rgba(59, 130, 246, 0.08);
          color: #3b82f6;
        }

        .global-date-field .p-datepicker .p-datepicker-buttonbar {
          padding: 12px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0 0 8px 8px;
        }

        .global-date-field .p-datepicker .p-datepicker-buttonbar button {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .global-date-field .p-datepicker .p-datepicker-buttonbar .p-button-text {
          color: #64748b;
        }

        .global-date-field .p-datepicker .p-datepicker-buttonbar .p-button-text:hover {
          background: rgba(100, 116, 139, 0.1);
          color: #1e3a8a;
        }

        .global-date-field .p-calendar.p-calendar-disabled .p-inputtext {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Inline calendar styles */
        .global-date-field .p-datepicker-inline {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
        }
      `}</style>
    </div>
  );
};